import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Twemoji from "react-twemoji";
import "./AdminPage.css";

import { ScopeType } from "sockapi/scope-types";
import { comparePuzzlesDefaultUnlockOrder } from "sockapi/scope-specs/hunt-admin-spec";
import { PuzzleMetadata, PuzzleData, PuzzleType } from "sockapi/puzzle-data";
import {
  useBrowserWidePersistentStateStore,
  useUpdateBrowserWidePersistentState,
} from "stores/BrowserWidePersistentState";
import {
  useSockClient,
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import {
  useScopeStateResolvable,
  useGlobalScopeState,
  useTeamScopeState,
  useNumTotalHintTokens,
  useNumUsedHintTokens,
  usePuzzleDisplayName,
} from "stores/ScopeStateStore";
import { useUpdatePieceInside } from "stores/RoundStore";
import { useNavStore, useRedirectIfNonAdmin } from "stores/NavStore";
import { useUpdateSeparatelyPersistedState } from "stores/UserPreferencesStore";
import { LargeSpinner, Spinner } from "components/Loading";
import Tooltip from "components/Tooltip";
import { typeIcon } from "components/Icon";
import { Link, PuzzleLink } from "components/Link";
import { makeTeamPagePath } from "components/TeamDisplayName";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import {
  BASE_FONT_SIZE_PX,
  BASE_OUTER_WRAPPER_WIDTH_PX,
  FONT_SIZE_SCALING_PER_ROUND,
} from "components/PuzzlePage/PuzzleBodyDisplay";
import { SyncedTextInput, SyncedNumberInput } from "components/synced-input";
import { PuzzleIcon } from "components/Icon";
import { clsObj } from "client-util/util";
import settings from "settings";
import Popover from "components/Popover";

export type PuzzleFilter = {
  types: { [type in PuzzleType]: boolean };
  round: string;
};
type FilterProps = {
  filter: PuzzleFilter;
  setFilter: (filter: PuzzleFilter) => void;
};
export const emptyPuzzleFilter = () => ({
  types: {
    [PuzzleType.PUZZLE]: true,
    [PuzzleType.PIECE]: true,
    [PuzzleType.STORY]: true,
  },
  round: "",
});
export const checkPuzzleFilter = (
  filter: PuzzleFilter,
  puzData: PuzzleMetadata
) =>
  filter.types[puzData.type] &&
  (!filter.round || filter.round === puzData.roundId);

export function AdminPuzzleFilter(props: FilterProps) {
  const { filter, setFilter } = props;
  const rounds = useGlobalScopeState(ScopeType.HUNT_ADMIN)?.rounds ?? {};
  return (
    <>
      <div className="button-squish-zone">
        {Object.entries(filter.types).map(([type, enabled]) => (
          <Tooltip
            key={type}
            text={`${enabled ? "Hide" : "Show"} ${type.toLowerCase()}`}
          >
            <button
              className={clsObj({ disabled: !enabled })}
              onClick={() =>
                setFilter({
                  ...filter,
                  types: {
                    ...filter.types,
                    [type]: !enabled,
                  },
                })
              }
            >
              <Twemoji>{typeIcon(type as PuzzleType)}</Twemoji>
            </button>
          </Tooltip>
        ))}
      </div>
      <select
        onChange={(evt) => setFilter({ ...filter, round: evt.target.value })}
      >
        <option value="">All rounds</option>
        {Object.values(rounds)
          .sort((a, b) => a.order - b.order)
          .map((round) => (
            <option key={round.roundId} value={round.roundId}>
              {round.displayName}
            </option>
          ))}
      </select>
    </>
  );
}

export function AdminNotes(props: { teamId: string; disabled?: boolean }) {
  const { teamId, disabled = false } = props;
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const sockClient = useSockClient();
  const teamAdminData = huntAdminScopeState?.teamAdminData[teamId] ?? null;

  if (!shouldDisplayAdmin || teamAdminData === null) return null;

  return (
    <span className="team-admin-notes button-squish-zone">
      {teamAdminData.adminNotes && (
        <Tooltip text={teamAdminData.adminNotes}>
          <button className="notes">
            <Twemoji>ℹ️</Twemoji>
          </button>
        </Tooltip>
      )}
      <Popover
        content={
          <>
            Admin-only notes:
            <SyncedTextInput
              style={{ width: "12em" }}
              disabled={disabled}
              syncedVal={teamAdminData.adminNotes ?? ""}
              onUpdate={(adminNotes) => {
                if (adminNotes === teamAdminData.adminNotes) return;
                sockClient.sendAdminScopeStep({
                  teams: { [teamId]: { adminNotes } },
                });
              }}
            />
          </>
        }
      >
        <button>
          <Twemoji>📝</Twemoji>
        </button>
      </Popover>
    </span>
  );
}

/** Used for both the admin and unlocks pages. */
export function AdminTopControl(props: { teamId?: string } & FilterProps) {
  const { teamId: overrideTeamId, filter, setFilter } = props;
  const ownTeamId = useTeamId();
  const teamId = overrideTeamId ?? ownTeamId;
  const sockClient = useSockClient();
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const teamAdminData = huntAdminScopeState?.teamAdminData ?? null;
  const teamAdminDataForTeam =
    teamId === null ? null : (teamAdminData?.[teamId] ?? null);
  const teamHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_HINTS,
    teamId === null ? null : { teamId }
  );
  const numTotalHintTokens = useNumTotalHintTokens(teamId);
  const numUsedHintTokens = useNumUsedHintTokens(teamId);
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const displayName = teamAdminDataForTeam?.displayName ?? "";
  const [displayNameInput, setDisplayNameInput] = useState("");

  useEffect(() => {
    if (displayName === null) return;
    // Keep the display name input in sync if the display name
    // changes due to external sources.
    setDisplayNameInput(displayName);
  }, [displayName, setDisplayNameInput]);

  if (
    teamId === null ||
    teamHintsScopeState === null ||
    numTotalHintTokens === null ||
    numUsedHintTokens === null ||
    teamAdminDataForTeam === null ||
    // Don't show as ready until we have synced the display name
    // into the input box.
    displayName === null
  )
    return <LargeSpinner />;

  const { userId } = teamAdminDataForTeam;

  const setDisplayNameFromInput = () => {
    if (displayNameInput === displayName) return;
    sockClient.sendAdminScopeStep({
      teams: {
        [teamId]: {
          displayName: displayNameInput,
        },
      },
    });
  };

  return (
    <div className="admin-top">
      <div className="admin-header">
        <label>
          Team name:{" "}
          <input
            type="text"
            style={{ width: "12em" }}
            value={displayNameInput}
            onChange={(e) => {
              setDisplayNameInput(e.target.value);
            }}
            onBlur={() => {
              setDisplayNameFromInput();
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                setDisplayNameFromInput();
              }
            }}
          />
        </label>
        {shouldDisplayAdmin ? (
          <>
            <Link route="impersonate" params={{ userId }}>
              <Tooltip text="Impersonate">
                <Twemoji tag="span">🕵️</Twemoji>
              </Tooltip>
            </Link>
            <Link
              route="admin_subpage"
              params={{ "*": `puzzles/team/${userId}` }}
            >
              <Tooltip text="Django admin [you should not need to use this]">
                <Twemoji tag="span">🐍️</Twemoji>
              </Tooltip>
            </Link>
            <Link route="certificate" params={{ teamId }}>
              <Tooltip text="Certificate">
                <Twemoji tag="span">🖼️</Twemoji>
              </Tooltip>
            </Link>
            <AdminNotes teamId={teamId} />
          </>
        ) : null}
      </div>
      <div className="admin-header">
        <label>
          <Tooltip text="Hint tokens">
            <Twemoji tag="span">🍞️ </Twemoji>
            <SyncedNumberInput
              min={0}
              syncedVal={teamHintsScopeState.numGrantedTokens}
              onUpdate={(val) => {
                sockClient.sendAdminScopeStep({
                  teams: {
                    [teamId]: {
                      overrideNumGrantedHintTokens: val,
                    },
                  },
                });
              }}
            />{" "}
            granted, {numTotalHintTokens} total, {numUsedHintTokens} spent
          </Tooltip>
        </label>
        <AdminPuzzleFilter filter={filter} setFilter={setFilter} />
      </div>
    </div>
  );
}

export function HackyAdminPinButton(props: {
  pinType: "pinnedPuzzles" | "pinnedTeams";
  entityId: string;
}) {
  const { pinType, entityId } = props;
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const pinnedEntities =
    useBrowserWidePersistentStateStore((state) => state.state[pinType]) ?? [];
  const isPinned = pinnedEntities.includes(entityId);
  const updateBrowserWidePersistentState =
    useUpdateBrowserWidePersistentState();

  if (!shouldDisplayAdmin) return null;

  return (
    <a
      className={clsObj({
        "pin-button": true,
        selected: isPinned,
      })}
      href="#"
      onClick={(evt) => {
        evt.preventDefault();
        updateBrowserWidePersistentState({
          [pinType]: isPinned
            ? pinnedEntities.filter((other) => other !== entityId)
            : [...pinnedEntities, entityId],
        });
      }}
    >
      <Tooltip
        text={`${isPinned ? "Unpin" : "Pin"} this ${
          {
            pinnedPuzzles: "puzzle. (You get notified harder on hints for it.)",
            pinnedTeams: "team. (They float to the top of the big board.)",
          }[pinType]
        }`}
      >
        <Twemoji>📌️</Twemoji>
      </Tooltip>
    </a>
  );
}

export function AdminPuzzleControl(props: {
  teamId?: string;
  puzName: string;
  parentPuzName?: string;
  readOnly?: true;
}) {
  const { teamId: overrideTeamId, puzName, parentPuzName, readOnly } = props;
  const ownTeamId = useTeamId();
  const teamId = overrideTeamId ?? ownTeamId;
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const teamScopeState = useTeamScopeState(teamId);
  /*
  const teamCannedHintsScopeState = useScopeStateResolvable(
    ScopeType.TEAM_CANNED_HINTS,
    teamId === null ? null : { teamId }
  );
  */
  const displayName = usePuzzleDisplayName(puzName, teamId);
  const sockClient = useSockClient();
  const updateInside = useUpdatePieceInside();

  if (
    teamId === null ||
    huntAdminScopeState === null ||
    teamScopeState === null ||
    //teamCannedHintsScopeState === null ||
    displayName === null
  )
    return <Spinner inline />;

  const adminPuzData = huntAdminScopeState.puzzles[puzName];
  const teamPuzData = teamScopeState.puzzles[puzName];
  const puzData = teamPuzData ?? adminPuzData;
  const isUnlocked = teamPuzData !== undefined;
  const isSolved = teamPuzData?.solveTime !== undefined;
  /*
  const { numTotal: numTotalCannedHints } = teamCannedHintsScopeState
    .cannedHints[puzName] ?? { numTotal: 0 };
  const maxNumCannedHints = huntAdminScopeState.numCannedHints[puzName] ?? 0;
  */
  const numExtraGuesses = teamScopeState.extraGuesses?.[puzName] ?? 0;
  const nameDom = (
    <>
      <PuzzleIcon puzData={puzData} />{" "}
      <PuzzleLink puzName={puzName}>{displayName}</PuzzleLink>
    </>
  );
  const roundData =
    puzData.roundId === undefined
      ? null
      : huntAdminScopeState.rounds[puzData.roundId];
  const aspectRatio = (() => {
    if (roundData === null) return 1;
    const roundFactor = roundData.gridCols / 2 / roundData.gridRows;
    switch (puzData.type) {
      case PuzzleType.PIECE:
        return (
          (puzData.cols ?? roundData.pieceCols) /
          (puzData.rows ?? roundData.pieceRows) /
          roundFactor
        );
      case PuzzleType.PUZZLE:
        return (
          (puzData.cols ?? roundData.boxCols) /
          (puzData.rows ?? roundData.boxRows) /
          roundFactor
        );
      case PuzzleType.STORY:
        return 1;
    }
  })();
  const [pieceWidth, pieceHeight] =
    aspectRatio > 1 ? [300, 300 / aspectRatio] : [300 * aspectRatio, 300];
  const pieceCols =
    puzData.cols ??
    (puzData.type === PuzzleType.PIECE ? roundData?.pieceCols : null) ??
    1;

  return (
    <div className="admin-puz">
      <div className="button-squish-zone">
        <Tooltip
          text={
            readOnly
              ? isUnlocked
                ? "Unlocked"
                : "Locked"
              : isUnlocked
                ? "Set locked"
                : "Set unlocked"
          }
        >
          <button
            onClick={() => {
              sockClient.sendAdminScopeStep({
                teams: {
                  [teamId]: {
                    overrideUnlocked: { [puzName]: !isUnlocked },
                  },
                },
              });
            }}
            disabled={readOnly}
          >
            <Twemoji tag="span">{isUnlocked ? "🔑" : "🔒"}</Twemoji>
          </button>
        </Tooltip>
        {puzData.type === PuzzleType.PIECE ? null : (
          <Tooltip
            text={
              readOnly
                ? isSolved
                  ? "Solved"
                  : "Unsolved"
                : isSolved
                  ? "Set unsolved"
                  : "Set solved"
            }
          >
            <button
              disabled={readOnly || !isUnlocked}
              onClick={() => {
                sockClient.sendAdminScopeStep({
                  teams: {
                    [teamId]: {
                      overrideSolved: { [puzName]: !isSolved },
                    },
                  },
                });
              }}
            >
              <Twemoji tag="span">{isSolved ? "✅️" : "🔲️"}</Twemoji>
            </button>
          </Tooltip>
        )}
      </div>
      {/*
      <label>
        <Tooltip text="Canned hints">
          <Twemoji tag="span">🥫 </Twemoji>
          <SyncedNumberInput
            syncedVal={numTotalCannedHints}
            onUpdate={(val) => {
              sockClient.sendAdminScopeStep({
                teams: {
                  [teamId]: {
                    overridePuzzleNumTotalCannedHints: { [puzName]: val },
                  },
                },
              });
            }}
            min={0}
            max={maxNumCannedHints}
            disabled={maxNumCannedHints <= 0}
          />
        </Tooltip>
      </label>
      */}
      {puzData.type === PuzzleType.PIECE &&
      !readOnly &&
      parentPuzName !== undefined ? (
        <div className="button-squish-zone">
          <Tooltip text="Add to puzzle">
            <button
              onClick={() => {
                updateInside({
                  pieceId: puzName,
                  boxId: parentPuzName,
                  add: true,
                }).catch(console.error);
              }}
            >
              <Twemoji tag="span">📥️</Twemoji>
            </button>
          </Tooltip>
          <Tooltip text="Remove from puzzle">
            <button
              onClick={() => {
                updateInside({
                  pieceId: puzName,
                  boxId: parentPuzName,
                  add: false,
                }).catch(console.error);
              }}
            >
              <Twemoji tag="span">📤️</Twemoji>
            </button>
          </Tooltip>
        </div>
      ) : null}
      {puzData.type === PuzzleType.PUZZLE &&
      !readOnly &&
      !settings.isPosthunt ? (
        <>
          <HackyAdminPinButton pinType="pinnedPuzzles" entityId={puzName} />
          <label>
            <Tooltip text="Extra guesses">
              <Twemoji tag="span">✍️ </Twemoji>
              <SyncedNumberInput
                syncedVal={numExtraGuesses}
                onUpdate={(val) => {
                  sockClient.sendAdminScopeStep({
                    teams: {
                      [teamId]: {
                        overridePuzzleNumExtraGuesses: { [puzName]: val },
                      },
                    },
                  });
                }}
                min={0}
                disabled={!puzData.hasAnswer}
              />
            </Tooltip>
          </label>
        </>
      ) : null}
      {puzData.type === PuzzleType.STORY || puzName === parentPuzName ? (
        <div>{nameDom}</div>
      ) : (
        <Tooltip
          text={
            <div
              style={{
                width: `${pieceWidth}px`,
                height: `${pieceHeight}px`,
                color: "var(--body-text-color)",
                overflow: "hidden",
                position: "relative",
              }}
              className="piece-view"
            >
              <PuzzleContentDisplay
                puzName={puzName}
                fontSize={`${(BASE_FONT_SIZE_PX / pieceCols) * (pieceWidth / BASE_OUTER_WRAPPER_WIDTH_PX) * (FONT_SIZE_SCALING_PER_ROUND[puzData.roundId ?? ""] ?? 1)}px`}
              />
            </div>
          }
        >
          {nameDom}
        </Tooltip>
      )}
    </div>
  );
}

export const sortAdminPagePuzzles = (puzzles: {
  [puzName: string]: PuzzleData;
}) => {
  return Object.values(puzzles).sort(comparePuzzlesDefaultUnlockOrder);
};

function TeamAdminDisplay(props: { displayedTeamId: string } & FilterProps) {
  const { displayedTeamId, filter, setFilter } = props;
  const ownTeamId = useTeamId();
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const teamAdminData = huntAdminScopeState?.teamAdminData ?? null;
  const { puzzles = null } = huntAdminScopeState ?? {};
  const sockClient = useSockClient();
  const teamAdminDataForTeam =
    displayedTeamId === null
      ? null
      : (teamAdminData?.[displayedTeamId] ?? null);
  const displayName = teamAdminDataForTeam?.displayName ?? "";

  if (
    displayedTeamId === null ||
    puzzles === null ||
    teamAdminDataForTeam === null ||
    displayName === null
  )
    return <LargeSpinner />;

  const { isHidden, isInactive, isTempBlocked } = teamAdminDataForTeam;
  const filteredPuzzles = sortAdminPagePuzzles(puzzles).filter((puzData) =>
    checkPuzzleFilter(filter, puzData)
  );
  const puzzlesDom = filteredPuzzles.map(({ puzName }) => {
    return (
      <AdminPuzzleControl
        key={puzName}
        teamId={displayedTeamId}
        puzName={puzName}
      />
    );
  });
  const dangerousConfirm = (): boolean => {
    if (!confirm("Are you sure?")) return false;
    if (ownTeamId === displayedTeamId) return true;
    const userRes = "ok";
    const userReq = `You are updating a different team. This is dangerous. Confirm the team name is ${displayName} and type “${userRes}” to proceed.`;
    if (prompt(userReq) !== userRes) return false;
    return true;
  };
  const bulkUpdate = (
    field: "overrideSolved" | "overrideUnlocked",
    value: boolean
  ) => {
    if (!dangerousConfirm()) return;
    sockClient.sendAdminScopeStep({
      teams: {
        [displayedTeamId]: {
          [field]: Object.fromEntries(
            filteredPuzzles
              .filter(
                ({ type }) =>
                  !(field === "overrideSolved" && type === PuzzleType.PIECE)
              )
              .map(({ puzName }) => [puzName, value])
          ),
        },
      },
    });
  };
  /*
  const updateCannedHints = (get: boolean) => {
    if (!dangerousConfirm()) return;
    sockClient.sendAdminScopeStep({
      teams: {
        [displayedTeamId]: {
          overridePuzzleNumTotalCannedHints: get
            ? maxNumCannedHints
            : Object.fromEntries(
                Object.keys(maxNumCannedHints).map((key) => [key, 0])
              ),
        },
      },
    });
  };
  */
  const reloadMovables = () => {
    sockClient.sendAdminScopeStep({
      reloadMovablesForTeam: displayedTeamId,
    });
  };
  return (
    <>
      <div className="admin-header">
        Team ID:{" "}
        <Link {...makeTeamPagePath(displayName)}>{displayedTeamId}</Link>
        <label>
          <input
            type="checkbox"
            checked={isHidden}
            onChange={(e) => {
              sockClient.sendAdminScopeStep({
                teams: {
                  [displayedTeamId]: {
                    isHidden: !isHidden,
                  },
                },
              });
            }}
          />{" "}
          <span
            title="Hides the team from the leaderboard. Used for suspected cheaters. They can still progress through the hunt."
            style={{ textDecoration: "underline", cursor: "help" }}
          >
            Hidden
          </span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={isInactive}
            onChange={(e) => {
              sockClient.sendAdminScopeStep({
                teams: {
                  [displayedTeamId]: {
                    isInactive: !isInactive,
                  },
                },
              });
            }}
          />{" "}
          <span
            title="Bans the team. They will be unable to access the hunt."
            style={{ textDecoration: "underline", cursor: "help" }}
          >
            Inactive
          </span>
        </label>
        <label>
          <input
            type="checkbox"
            checked={isTempBlocked}
            onChange={(e) => {
              sockClient.sendAdminScopeStep({
                teams: {
                  [displayedTeamId]: {
                    isTempBlocked: !isTempBlocked,
                  },
                },
              });
            }}
          />{" "}
          <span
            title="Temporarily blocks the team, in case we need to log in and change their state. Uncheck this afterwards."
            style={{ textDecoration: "underline", cursor: "help" }}
          >
            Blocked
          </span>
        </label>
      </div>
      <AdminTopControl
        teamId={displayedTeamId}
        filter={filter}
        setFilter={setFilter}
      />
      <div className="admin-puzzles">{puzzlesDom}</div>
      <div className="admin-header">
        <div className="button-squish-zone">
          <Tooltip text="Lock all">
            <button onClick={() => bulkUpdate("overrideUnlocked", false)}>
              <Twemoji tag="span">🔒</Twemoji>
            </button>
          </Tooltip>
          <Tooltip text="Unlock all">
            <button onClick={() => bulkUpdate("overrideUnlocked", true)}>
              <Twemoji tag="span">🔑</Twemoji>
            </button>
          </Tooltip>
        </div>
        <div className="button-squish-zone">
          <Tooltip text="Unsolve all">
            <button onClick={() => bulkUpdate("overrideSolved", false)}>
              <Twemoji tag="span">🔲️</Twemoji>
            </button>
          </Tooltip>
          <Tooltip text="Solve all">
            <button onClick={() => bulkUpdate("overrideSolved", true)}>
              <Twemoji tag="span">✅️</Twemoji>
            </button>
          </Tooltip>
        </div>
        {/*
        <div className="button-squish-zone">
          <Tooltip text="Hide all canned hints">
            <button onClick={() => updateCannedHints(false)}>
              <Twemoji tag="span">🗑️</Twemoji>
            </button>
          </Tooltip>
          <Tooltip text="Get all canned hints">
            <button onClick={() => updateCannedHints(true)}>
              <Twemoji tag="span">🥫️</Twemoji>
            </button>
          </Tooltip>
        </div>
        */}
        <div className="button-squish-zone">
          <Tooltip text="Reload the movables">
            <button onClick={reloadMovables}>
              <Twemoji tag="span">📦</Twemoji>
            </button>
          </Tooltip>
        </div>
        <button
          onClick={() => {
            sockClient
              .sendAdminScopeStepAndWaitAsync({
                reloadPuzzles: true,
              })
              .then((respPayload) => {
                if (respPayload === null) return;
                toastr.success("PuzzlesDB reloaded!");
              })
              .catch((err) => {
                console.error(err);
                toastr.error("Failed to reload PuzzlesDB.");
              });
          }}
        >
          <Twemoji tag="span">♻️ Global PuzzlesDB Refresh</Twemoji>
        </button>
      </div>
    </>
  );
}

function AdminPage() {
  const { teamId: displayedTeamId = null } = useParams<{
    teamId?: string;
  }>();
  const ownTeamId = useTeamId();
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  // Defaulting to your own team always seems more intuitive now.
  // const adminPageLastSeenTeamId = useSeparatelyPersistedState(
  //   (state) => state.adminPageLastSeenTeamId
  // );
  const adminPageLastSeenTeamId = undefined;
  const teamAdminData = huntAdminScopeState?.teamAdminData ?? null;
  const updateSeparatelyPersistedState = useUpdateSeparatelyPersistedState();
  const [searchText, setSearchText] = useState("");
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const teamAdminDataForTeam =
    displayedTeamId === null
      ? null
      : (teamAdminData?.[displayedTeamId] ?? null);
  const [filter, setFilter] = useState(emptyPuzzleFilter());

  useRedirectIfNonAdmin();

  // If the last seen team disappears (such as during dev), reset
  // to the current team.
  useEffect(() => {
    if (displayedTeamId === null) return;
    if (teamAdminData === null) return;
    if (teamAdminDataForTeam !== null) return;
    if (ownTeamId === null) return;
    setRedirectRoute({
      route: "sock_admin_team",
      params: { teamId: ownTeamId },
      replace: true,
    });
  }, [
    displayedTeamId,
    teamAdminData,
    teamAdminDataForTeam,
    ownTeamId,
    setRedirectRoute,
  ]);

  // Automatically select the last seen team, otherwise the own team.
  useEffect(() => {
    if (displayedTeamId !== null) return;
    if (ownTeamId === null) return;
    setRedirectRoute({
      route: "sock_admin_team",
      params: { teamId: adminPageLastSeenTeamId ?? ownTeamId },
      replace: true,
    });
  }, [displayedTeamId, adminPageLastSeenTeamId, ownTeamId, setRedirectRoute]);

  useEffect(() => {
    if (displayedTeamId === null) return;
    if (teamAdminDataForTeam === null) return;
    updateSeparatelyPersistedState({
      adminPageLastSeenTeamId: displayedTeamId,
    });
  }, [displayedTeamId, teamAdminDataForTeam, updateSeparatelyPersistedState]);

  if (
    teamAdminData === null ||
    teamAdminDataForTeam === null ||
    displayedTeamId === null
  )
    return <LargeSpinner />;

  const teamsDom = Object.keys(teamAdminData)
    .filter((teamId) => {
      const teamData = teamAdminData[teamId];
      return teamData.displayName.toLowerCase().includes(searchText.trim());
    })
    .sort()
    .map((teamId) => {
      const teamData = teamAdminData[teamId];
      const isSelected = displayedTeamId === teamId;
      return (
        <button
          key={teamId}
          className={`admin-team-btn${isSelected ? " highlight" : ""}`}
          title={teamData.displayName}
          onClick={() => {
            setRedirectRoute({
              route: "sock_admin_team",
              params: { teamId },
              replace: true,
            });
          }}
        >
          {teamData.displayName}
        </button>
      );
    });

  return (
    <div className="admin-root">
      <div className="admin-sidebar">
        <h1>
          <Twemoji tag="span">🧦️</Twemoji>Admin
        </h1>
        <input
          type="text"
          onChange={(e) => {
            setSearchText(e.target.value.toLowerCase().trim());
          }}
        />
        <div className="admin-teams">{teamsDom}</div>
      </div>
      <div className="admin-main">
        <TeamAdminDisplay
          displayedTeamId={displayedTeamId}
          filter={filter}
          setFilter={setFilter}
        />
      </div>
    </div>
  );
}

export function AdminRedirectPage() {
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);

  useEffect(() => {
    setRedirectRoute({ route: "sock_admin", replace: true });
  }, [setRedirectRoute]);

  return <LargeSpinner />;
}

export default AdminPage;
